import { DialogActions, ThemeProvider } from '@mui/material';
import { createTheme } from '@mui/material/styles';
import { makeStyles } from '@mui/styles';
import AddIcon from '@mui/icons-material/Add';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import DeleteIcon from '@mui/icons-material/Delete';
import HelpIcon from '@mui/icons-material/Help';
import { Alert, MenuItem, Paper, Snackbar, Stack, Tooltip, Typography, Grid } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { randomId } from '@mui/x-data-grid-generator';
import {
    DataGridPremium,
    GridCsvExportMenuItem,
    GridExcelExportMenuItem,
    gridFilteredSortedRowIdsSelector,
    GridToolbarColumnsButton,
    GridToolbarContainer,
    GridToolbarDensitySelector,
    GridToolbarExportContainer,
    GridToolbarFilterButton,
    useGridApiRef,
} from '@mui/x-data-grid-premium';
import _ from 'lodash';
import { useEffect, useState, useRef } from 'react';
import { emitCustomEvent } from 'react-custom-events';
import { useSelector } from 'react-redux';
import Events from '../../../defaults/Events';
import FieldNames from '../../../defaults/FieldNames.json';
import ErrorService from '../../../services/ErrorService';
import FeatureService from '../../../services/FeatureService';
import FundingService from '../../../services/FundingService';
import UserHelperService from '../../../services/UserHelperService';
import { formatDateForDisplay, getSaveableDate } from '../../../utils/DateHelper';
import { getAllMissingFieldsCount, getStatus } from '../../../utils/FeatureHelper';
import { CalculateDefaultFundingAcres } from '../../../utils/FundingHelper';
import { MuiMenuDropdown } from '../../popover/MuiMenuDropdown/MuiMenuDropdown';
import InFormAlert from '../../InFormAlert';
import InFormGenericModal from '../../InFormGenericModal';
import RecordCostAssignment from '../../userPopover/RecordCostAssignment';
import RecordFundingAssignment from '../../userPopover/RecordFundingAssignment';
import RecordFundingSourceAssignment from '../../userPopover/RecordFundingSourceAssignment';
import FundingAcresPopup from './FundingAcresPopup';
import { currencyFormatter, removeCommas } from '../../../utils/FormatHelper';
import ColorConstants from '../../../defaults/ColorConstants';
import {
    getChangedValuesFromObject,
    getLogForFieldUpdatesOnExistingFeature,
} from '../../../utils/AuditLogHelper';
import AuditLogService from '../../../services/AuditLogService';
import { setShowGlobalLoader } from '../../../redux/slices/globalContextSlice';
import { useDispatch } from 'react-redux';

const theme = createTheme({
    overrides: {
        MuiPaper: {
            root: {
                backgroundColor: 'green',
            },
        },
        MuiOutlinedInput: {
            root: {
                '& $notchedOutline': {
                    //initial border
                    borderColor: ColorConstants.BlueAlien,
                },
                '&:hover $notchedOutline': {
                    //border on hover
                    borderColor: ColorConstants.BlueAlien,
                    border: '2px solid',
                },
                '&$focused $notchedOutline': {
                    //border on focus
                    borderColor: ColorConstants.BlueAlien,
                    border: '2px solid',
                },
            },
        },
    },
});

const useStyles = makeStyles({
    underline: {
        '&&&:before': {
            borderBottom: '2px solid red',
        },
        '&&:after': {
            borderBottom: '2px solid red',
        },
    },
    condensedFormControl: {
        margin: '5px 15px 5px 15px',
        minWidth: 120,
        width: '90%',
    },
    formControl: {
        margin: 15,
        minWidth: 120,
        width: '90%',
    },
    snackBarStyle: {
        top: '120px !important',
    },
});

export default function FundingCostGrid({
    editableFeature,
    setEditableFeature,
    userInfo,
    mode,
    userRoleInfo,
    isApproved,
    rederiveFeatureInfo,
    setLocationDepartmentValue,
    setLocationAgencyValue,
    setLocationRegionValue,
    setLocationUnitValue,
    featureUnsavedFields,
    setBaseFeatureAttributes,
}) {
    const dispatch = useDispatch();
    const config = useSelector((state) => state.configuration.config);
    const rootServiceUrl = useSelector((state) => state.configuration.rootServiceUrl);
    const nifcToken = useSelector((state) => state.configuration.nifcToken);

    const gridActionMenuRef = useRef();

    const apiRef = useGridApiRef();

    const classes = useStyles();

    const [rows, setRows] = useState([]);
    const [rowModesModel, setRowModesModel] = useState({});
    const [openFundingCostEdit, setOpenFundingCostEdit] = useState(false);
    const [openFundingAcresEdit, setOpenFundingAcresEdit] = useState(false);
    const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false);
    const [resetAcresPopupOpen, setResetAcresPopupOpen] = useState(false);
    const [departmentSelection, setDepartmentSelection] = useState('');
    const [agencySelection, setAgencySelection] = useState('');
    const [regionSelection, setRegionSelection] = useState('');
    const [unitSelection, setUnitSelection] = useState('');
    const [subUnitSelection, setSubUnitSelection] = useState('');
    const [tribeSelection, setTribeSelection] = useState('');
    const [fundingUnitTypeSelection, setFundingUnitTypeSelection] = useState('');
    const [costCenterCodeSelection, setCostCenterCodeSelection] = useState('');
    const [functionalAreaSelection, setFunctionalAreaSelection] = useState('');
    const [fundingUnitIDSelection, setFundingUnitIDSelection] = useState('');
    const [selectedRow, setSelectedRow] = useState();
    const [fundingSourceSelection, setFundingSourceSelection] = useState('');
    const [fundingSourceProgramSelection, setFundingSourceProgramSelection] = useState('');
    const [fundingSourceCategorySelection, setFundingSourceCategorySelection] = useState('');
    const [fundingSourceSubCategorySelection, setFundingSourceSubCategorySelection] = useState('');
    const [contributedNotesSelection, setContributedNotesSelection] = useState('');
    const [personnelCost, setPersonnelCost] = useState(0);
    const [otherCost, setOtherCost] = useState(0);
    const [assetCost, setAssetCost] = useState(0);
    const [grantsFixedCost, setGrantsFixedCost] = useState(0);
    const [contractualCost, setContractualCost] = useState(0);
    const [totalCost, setTotalCost] = useState(0);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');
    const [isNew, setIsNew] = useState(false);
    const [isBil, setIsBil] = useState(false);
    const [isBar, setIsBar] = useState(false);
    const [fundingSourceValid, setFundingSourceValid] = useState(false);
    const [fundingValid, setFundingValid] = useState(false);
    const [fundingCostValid, setFundingCostValid] = useState(false);
    const [isFundingRowSaving, setIsFundingRowSaving] = useState(false);
    const [isFundingFeatureSaving, setIsFundingFeatureSaving] = useState(false);
    const [isFundingQuerying, setIsFundingQuerying] = useState(false);

    const isInteragencyRow = fundingSourceCategorySelection === 'Interagency Support';

    const isTreatment =
        editableFeature?.attributes?.[FieldNames.featureFields.class] ===
        FieldNames.classTypes.treatment
            ? true
            : false;

    const isActual =
        editableFeature?.attributes?.[FieldNames.featureFields.class] ===
            FieldNames.classTypes.actualTreatment ||
        editableFeature?.attributes?.[FieldNames.featureFields.class] ===
            FieldNames.classTypes.actualActivity
            ? true
            : false;

    const isPoint =
        editableFeature?.attributes?.[FieldNames.featureFields.isPoint] === FieldNames.yesNo.yes
            ? true
            : false;

    const isFundingCostLoading = isFundingRowSaving || isFundingFeatureSaving || isFundingQuerying;

    const requiredFields = isTreatment
        ? isActual
            ? FieldNames.actualTreatmentRequiredFields
            : FieldNames.requiredFields
        : isActual
          ? FieldNames.actualActivityRequiredFields
          : FieldNames.activityRequiredFields;

    useEffect(() => {
        if (isNew && userRoleInfo) {
            let departmentSelectionFinal = '';
            let agencySelectionFinal = '';
            let regionSelectionFinal = '';
            let unitSelectionFinal = '';
            let subUnitSelectionFinal = '';
            let tribeSelectionFinal = '';
            if (userRoleInfo.length === 1) {
                departmentSelectionFinal = userRoleInfo[0][FieldNames.userRoles.department]
                    ? userRoleInfo[0][FieldNames.userRoles.department]
                    : '';
                agencySelectionFinal = userRoleInfo[0][FieldNames.userRoles.agency]
                    ? userRoleInfo[0][FieldNames.userRoles.agency]
                    : '';
                regionSelectionFinal = userRoleInfo[0][FieldNames.userRoles.region]
                    ? userRoleInfo[0][FieldNames.userRoles.region]
                    : '';
                unitSelectionFinal = userRoleInfo[0][FieldNames.userRoles.unit]
                    ? userRoleInfo[0][FieldNames.userRoles.unit]
                    : '';
                subUnitSelectionFinal = userRoleInfo[0][FieldNames.userRoles.subUnit]
                    ? userRoleInfo[0][FieldNames.userRoles.subUnit]
                    : '';
                tribeSelectionFinal = userRoleInfo[0][FieldNames.userRoles.tribe]
                    ? userRoleInfo[0][FieldNames.userRoles.tribe]
                    : '';
            } else {
                let initialDepartments = [];
                let initialAgencies = [];
                let initialRegions = [];
                let initialUnits = [];
                let initialSubUnits = [];
                let initialTribes = [];
                userRoleInfo.forEach((role) => {
                    if (
                        role[FieldNames.userRoles.department] &&
                        initialDepartments &&
                        !initialDepartments.includes(role[FieldNames.userRoles.department])
                    ) {
                        initialDepartments.push(role[FieldNames.userRoles.department]);
                    }
                    if (
                        role[FieldNames.userRoles.agency] &&
                        initialAgencies &&
                        !initialAgencies.includes(role[FieldNames.userRoles.agency])
                    ) {
                        initialAgencies.push(role[FieldNames.userRoles.agency]);
                    }
                    if (
                        role[FieldNames.userRoles.region] &&
                        initialRegions &&
                        !initialRegions.includes(role[FieldNames.userRoles.region])
                    ) {
                        initialRegions.push(role[FieldNames.userRoles.region]);
                    }
                    if (
                        role[FieldNames.userRoles.unit] &&
                        initialUnits &&
                        !initialUnits.includes(role[FieldNames.userRoles.unit])
                    ) {
                        initialUnits.push(role[FieldNames.userRoles.unit]);
                    }
                    if (
                        role[FieldNames.userRoles.subUnit] &&
                        initialSubUnits &&
                        !initialSubUnits.includes(role[FieldNames.userRoles.subUnit])
                    ) {
                        initialSubUnits.push(role[FieldNames.userRoles.subUnit]);
                    }
                    if (
                        role[FieldNames.userRoles.tribe] &&
                        initialTribes &&
                        !initialTribes.includes(role[FieldNames.userRoles.tribe])
                    ) {
                        initialTribes.push(role[FieldNames.userRoles.tribe]);
                    }
                    //Not pulling UnitTypes due to looking it up again.
                });

                if (initialDepartments && initialDepartments.length > 0) {
                    if (initialDepartments.length === 1) {
                        departmentSelectionFinal = initialDepartments[0];
                    } else if (initialDepartments.length > 1) {
                        departmentSelectionFinal = '';
                    }
                } else {
                    departmentSelectionFinal = '';
                }

                if (initialAgencies && initialAgencies.length > 0) {
                    if (initialAgencies.length === 1) {
                        agencySelectionFinal = initialAgencies[0];
                    } else if (initialAgencies.length > 1) {
                        agencySelectionFinal = '';
                    }
                } else {
                    agencySelectionFinal = '';
                }

                if (initialRegions && initialRegions.length > 0) {
                    if (initialRegions.length === 1) {
                        regionSelectionFinal = initialRegions[0];
                    } else if (initialRegions.length > 1) {
                        regionSelectionFinal = '';
                    }
                } else {
                    regionSelectionFinal = '';
                }

                if (initialUnits && initialUnits.length > 0) {
                    if (initialUnits.length === 1) {
                        unitSelectionFinal = initialUnits[0];
                    } else if (initialUnits.length > 1) {
                        unitSelectionFinal = '';
                    }
                } else {
                    unitSelectionFinal = '';
                }

                if (initialSubUnits && initialSubUnits.length > 0) {
                    if (initialSubUnits.length === 1) {
                        subUnitSelectionFinal = initialSubUnits[0];
                    } else if (initialSubUnits.length > 1) {
                        subUnitSelectionFinal = '';
                    }
                } else {
                    subUnitSelectionFinal = '';
                }

                if (initialTribes && initialTribes.length > 0) {
                    if (initialTribes.length === 1) {
                        tribeSelectionFinal = initialTribes[0];
                    } else if (initialTribes.length > 1) {
                        tribeSelectionFinal = '';
                    }
                } else {
                    tribeSelectionFinal = '';
                }
            }
            UserHelperService.getUnitEntryByAllFields(
                departmentSelectionFinal,
                agencySelectionFinal,
                regionSelectionFinal,
                unitSelectionFinal,
                subUnitSelectionFinal,
                tribeSelectionFinal
            )
                .then((resp) => {
                    setDepartmentSelection(departmentSelectionFinal);
                    setAgencySelection(agencySelectionFinal);
                    setRegionSelection(regionSelectionFinal);
                    setUnitSelection(unitSelectionFinal);
                    setSubUnitSelection(subUnitSelectionFinal);
                    setTribeSelection(tribeSelectionFinal);
                    if (resp && resp.length > 0) {
                        setFundingUnitTypeSelection(resp[0][FieldNames.units.unitType]);
                        setFunctionalAreaSelection('XXXXXXXXX.XXXXXX');
                        setCostCenterCodeSelection(resp[0][FieldNames.units.costCenterCode]);
                        setFundingUnitIDSelection(resp[0][FieldNames.units.unitID]);
                    } else {
                        setFundingUnitTypeSelection('');
                        setFunctionalAreaSelection('XXXXXXXXX.XXXXXX');
                        setCostCenterCodeSelection('XXXXXXXXXX');
                        setFundingUnitIDSelection('');
                    }
                })
                .catch((error) => {
                    ErrorService.addErrorLogEntry(
                        userInfo[FieldNames.userFields.nifcUsername],
                        error
                    );
                });
        }
    }, [config, isNew, rootServiceUrl, userRoleInfo, nifcToken, userInfo]);

    const getFundingCostByFeatureID = async () => {
        let newRows = [];
        try {
            setIsFundingQuerying(true);
            const joinID = editableFeature.attributes[FieldNames.featureFields.joinID];
            const joinFeature = editableFeature.attributes[FieldNames.featureFields.joinFeature]
                ? editableFeature.attributes[FieldNames.featureFields.joinFeature]
                : FieldNames.joinFeatureValues.polygon;
            const allFundingForFeature = await FundingService.getFundingByJoinID(
                joinID,
                joinFeature
            );
            let fundingObjectIds = [];
            allFundingForFeature.forEach((fundingItem) => {
                if (
                    fundingItem[FieldNames.fundingFields.fundingIsManual] !== FieldNames.yesNo.yes
                ) {
                    fundingObjectIds.push(fundingItem[FieldNames.fundingFields.objectID]);
                }

                const obj = {
                    [FieldNames.fundingFields.id]: fundingItem[FieldNames.fundingFields.id],
                    featureId: editableFeature.attributes[FieldNames.featureFields.id],
                    featureObjectId: editableFeature.attributes[FieldNames.featureFields.objectID],
                    [FieldNames.fundingFields.objectID]:
                        fundingItem[FieldNames.fundingFields.objectID],
                    [FieldNames.fundingFields.joinID]: joinID,
                    [FieldNames.fundingFields.joinFeature]: joinFeature,
                    [FieldNames.fundingAcreageFields.acres]:
                        fundingItem[FieldNames.fundingAcreageFields.acres] ?? 0,
                    [FieldNames.fundingFields.fundingSource]:
                        fundingItem[FieldNames.fundingFields.fundingSource],
                    [FieldNames.fundingFields.fundingSourceProgram]:
                        fundingItem[FieldNames.fundingFields.fundingSourceProgram],
                    [FieldNames.fundingFields.fundingSourceCategory]:
                        fundingItem[FieldNames.fundingFields.fundingSourceCategory],
                    [FieldNames.fundingFields.fundingSourceSubCategory]:
                        fundingItem[FieldNames.fundingFields.fundingSourceSubCategory],
                    [FieldNames.fundingFields.fundingDepartment]:
                        fundingItem[FieldNames.fundingFields.fundingDepartment],
                    [FieldNames.fundingFields.fundingAgency]:
                        fundingItem[FieldNames.fundingFields.fundingAgency],
                    [FieldNames.fundingFields.fundingRegion]:
                        fundingItem[FieldNames.fundingFields.fundingRegion],
                    [FieldNames.fundingFields.fundingUnit]:
                        fundingItem[FieldNames.fundingFields.fundingUnit],
                    [FieldNames.fundingFields.fundingSubUnit]:
                        fundingItem[FieldNames.fundingFields.fundingSubUnit],
                    [FieldNames.fundingFields.fundingTribe]:
                        fundingItem[FieldNames.fundingFields.fundingTribe],
                    [FieldNames.fundingFields.fundingUnitType]:
                        fundingItem[FieldNames.fundingFields.fundingUnitType],
                    [FieldNames.fundingFields.fundingUnitID]:
                        fundingItem[FieldNames.fundingFields.fundingUnitID],
                    [FieldNames.fundingFields.costCenter]:
                        fundingItem[FieldNames.fundingFields.costCenter],
                    [FieldNames.fundingFields.costCode]:
                        fundingItem[FieldNames.fundingFields.costCode],
                    [FieldNames.fundingFields.functionalArea]:
                        fundingItem[FieldNames.fundingFields.functionalArea],
                    [FieldNames.fundingFields.assetCost]:
                        fundingItem[FieldNames.fundingFields.assetCost],
                    [FieldNames.fundingFields.contractualCost]:
                        fundingItem[FieldNames.fundingFields.contractualCost],
                    [FieldNames.fundingFields.grantsFixedCost]:
                        fundingItem[FieldNames.fundingFields.grantsFixedCost],
                    [FieldNames.fundingFields.otherCost]:
                        fundingItem[FieldNames.fundingFields.otherCost],
                    [FieldNames.fundingFields.personnelCost]:
                        fundingItem[FieldNames.fundingFields.personnelCost],
                    [FieldNames.fundingFields.totalCost]:
                        fundingItem[FieldNames.fundingFields.totalCost],
                    [FieldNames.fundingFields.isBIL]: fundingItem[FieldNames.fundingFields.isBIL],
                    [FieldNames.fundingFields.isBAR]: fundingItem[FieldNames.fundingFields.isBAR],
                    [FieldNames.fundingFields.notes]: fundingItem[FieldNames.fundingFields.notes],
                    [FieldNames.fundingFields.fundingIsManual]:
                        fundingItem[FieldNames.fundingFields.fundingIsManual],
                };
                newRows.push(obj);
            });
            setRows(newRows);
        } catch (error) {
            ErrorService.addErrorLogEntry(userInfo[FieldNames.userFields.nifcUsername], error);
        } finally {
            setIsFundingQuerying(false);
            return newRows;
        }
    };

    useEffect(() => {
        if (editableFeature?.attributes?.[FieldNames.featureFields.objectID]) {
            getFundingCostByFeatureID().catch((error) => {
                ErrorService.addErrorLogEntry(userInfo[FieldNames.userFields.nifcUsername], error);
            });
        }
    }, [userInfo, editableFeature]);

    const handleRowModesModelChange = (newRowModesModel) => {
        setRowModesModel(newRowModesModel);
    };

    const displayableFields = [
        'actions',
        FieldNames.fundingFields.id,
        'featureObjectId', // probably delete
        'featureId',
        FieldNames.fundingFields.fundingSource,
        FieldNames.fundingFields.fundingSourceProgram,
        FieldNames.fundingFields.fundingSourceCategory,
        FieldNames.fundingFields.fundingSourceSubCategory,
        FieldNames.fundingFields.fundingDepartment,
        FieldNames.fundingFields.fundingAgency,
        FieldNames.fundingFields.fundingRegion,
        FieldNames.fundingFields.fundingUnit,
        FieldNames.fundingFields.fundingSubUnit,
        FieldNames.fundingFields.fundingTribe,
        FieldNames.fundingFields.fundingUnitType,
        FieldNames.fundingFields.fundingUnitID,
        FieldNames.fundingFields.personnelCost,
        FieldNames.fundingFields.grantsFixedCost,
        FieldNames.fundingFields.assetCost,
        FieldNames.fundingFields.contractualCost,
        FieldNames.fundingFields.otherCost,
        FieldNames.fundingFields.totalCost,
        FieldNames.fundingAcreageFields.acres,
        FieldNames.fundingFields.modifiedBy,
        FieldNames.fundingFields.modifiedDate,
        FieldNames.fundingFields.createdBy,
        FieldNames.fundingFields.createdDate,
        FieldNames.fundingFields.notes,
    ];

    const formatResponse = (row, element) => {
        if (row) {
            if (
                element === FieldNames.fundingFields.createdDate ||
                element === FieldNames.fundingFields.modifiedDate
            ) {
                if (row[element]) {
                    if (typeof row[element] === 'number') {
                        return `${formatDateForDisplay(row[element])}`;
                    } else if (typeof row[element] === 'string') {
                        //Turn
                        return `${formatDateForDisplay(row[element])}`;
                    } else if (typeof row[element] === 'object') {
                        return `${formatDateForDisplay(row[element])}`;
                    } else {
                        return `${row[element]}`;
                    }
                } else {
                    return ``;
                }
            } else if (
                element === FieldNames.featureFields.estimatedTotalCost ||
                element === FieldNames.featureFields.estimatedAssetCost ||
                element === FieldNames.featureFields.estimatedContractualCost ||
                element === FieldNames.featureFields.estimatedGrantsFixedCost ||
                element === FieldNames.featureFields.estimatedOtherCost ||
                element === FieldNames.featureFields.estimatedPersonnelCost ||
                element === FieldNames.featureFields.contributedTotalCost
            ) {
                if (row[element]) {
                    return `$${row[element]}`;
                } else {
                    return ``;
                }
            } else {
                if (row[element]) {
                    return `${row[element]}`;
                } else {
                    return ``;
                }
            }
        } else {
            return ``;
        }
    };

    let columns = [];

    displayableFields.forEach((element) => {
        if (element === 'featureObjectId' || element === 'featureId') {
            let header = '';
            if (element === 'featureObjectId') {
                header = 'Feature ObjectID';
            } else if (element === 'featureId') {
                header = 'Feature ID';
            }
            columns.push({
                field: element,
                headerName: header,
                hideable: true,
                sortable: true,
                editable: false,
                width: 80,
                renderCell: (value, row) => formatResponse(row, element),
            });
        } else if (
            element === FieldNames.fundingFields.fundingUnitType ||
            element === FieldNames.fundingFields.fundingSourceProgram ||
            element === FieldNames.fundingFields.fundingSourceCategory ||
            element === FieldNames.fundingFields.fundingSourceSubCategory ||
            element === FieldNames.fundingFields.fundingDepartment ||
            element === FieldNames.fundingFields.fundingSource ||
            element === FieldNames.fundingFields.fundingSubUnit ||
            element === FieldNames.fundingFields.fundingAgency ||
            element === FieldNames.fundingFields.fundingTribe ||
            element === FieldNames.fundingFields.fundingRegion ||
            element === FieldNames.fundingFields.fundingUnit ||
            element === FieldNames.fundingFields.notes ||
            element === FieldNames.fundingFields.fundingUnitID ||
            element === FieldNames.fundingAcreageFields.acres
        ) {
            let headerName = '';
            let columnWidth = 80;
            if (element === FieldNames.fundingFields.fundingUnitType) {
                headerName = 'Funding Unit Type';
                columnWidth = 110;
            } else if (element === FieldNames.fundingFields.fundingUnitID) {
                headerName = 'Funding Unit ID';
                columnWidth = 100;
            } else if (element === FieldNames.fundingFields.fundingDepartment) {
                headerName = 'Department';
                columnWidth = 180;
            } else if (element === FieldNames.fundingFields.fundingSubUnit) {
                headerName = 'SubUnit';
                columnWidth = 180;
            } else if (element === FieldNames.fundingFields.fundingTribe) {
                headerName = 'Tribe';
                columnWidth = 180;
            } else if (element === FieldNames.fundingFields.fundingRegion) {
                headerName = 'Region';
                columnWidth = 180;
            } else if (element === FieldNames.fundingFields.fundingAgency) {
                headerName = 'Agency';
                columnWidth = 80;
            } else if (element === FieldNames.fundingFields.fundingUnit) {
                headerName = 'Unit';
                columnWidth = 180;
            } else if (element === FieldNames.fundingFields.fundingSource) {
                headerName = 'Funding Source';
                columnWidth = 180;
            } else if (element === FieldNames.fundingFields.fundingSourceProgram) {
                headerName = 'Program';
                columnWidth = 180;
            } else if (element === FieldNames.fundingFields.fundingSourceCategory) {
                headerName = 'Category';
                columnWidth = 180;
            } else if (element === FieldNames.fundingFields.fundingSourceSubCategory) {
                headerName = 'SubCategory';
                columnWidth = 180;
            } else if (element === FieldNames.fundingFields.notes) {
                headerName = 'Notes';
                columnWidth = 200;
            } else if (element === FieldNames.fundingAcreageFields.acres) {
                headerName = 'Acres';
                columnWidth = 100;
            }

            columns.push({
                field: element,
                headerName: headerName,
                type: 'text',
                hideable: true,
                sortable: true,
                editable: false,
                width: columnWidth,
                valueGetter: (value, row) => formatResponse(row, element),
            });
        } else if (
            element === FieldNames.fundingFields.totalCost ||
            element === FieldNames.fundingFields.assetCost ||
            element === FieldNames.fundingFields.contractualCost ||
            element === FieldNames.fundingFields.grantsFixedCost ||
            element === FieldNames.fundingFields.otherCost ||
            element === FieldNames.fundingFields.personnelCost
        ) {
            let headerName = '';
            let columnWidth = 80;
            if (element === FieldNames.fundingFields.assetCost) {
                headerName = 'Asset Cost';
            } else if (element === FieldNames.fundingFields.contractualCost) {
                headerName = 'Contractual Cost';
                columnWidth = 150;
            } else if (element === FieldNames.fundingFields.grantsFixedCost) {
                headerName = 'Grants Fixed Cost';
                columnWidth = 150;
            } else if (element === FieldNames.fundingFields.otherCost) {
                headerName = 'Other Cost';
            } else if (element === FieldNames.fundingFields.personnelCost) {
                headerName = 'Personnel Cost';
                columnWidth = 150;
            } else if (element === FieldNames.fundingFields.totalCost) {
                headerName = 'Total Cost';
                columnWidth = 150;
            }
            columns.push({
                field: element,
                headerName: headerName,
                sortable: true,
                editable: false,
                type: 'number',
                width: columnWidth,
                valueFormatter: (value) => {
                    if (value === 0) {
                        return '$0';
                    } else {
                        if (!value) {
                            return '';
                        } else {
                            return currencyFormatter.format(value);
                        }
                    }
                },
            });
        } else if (element === 'ID') {
            columns.push({
                field: 'ID',
                headerName: 'ID',
                sortable: true,
                editable: false,
            });
        } else if (element === 'actions') {
            columns.push({
                field: 'actions',
                headerName: 'Actions',
                hideable: true,
                sortable: false,
                editable: false,
                width: 180,
                align: 'left',
                disableExport: true,
                // Render Cell allows you to set entire components within a cell for custom buttons/functionality
                renderCell: (params) => {
                    if (params.rowNode.type === 'pinnedRow') {
                        if (mode === 'edit' && !isApproved) {
                            return (
                                <Tooltip title='Add a new line of cost here'>
                                    <span>
                                        <Button
                                            onClick={(e) => {
                                                setIsNew(true);
                                                setSelectedRow(null);
                                                setOpenFundingCostEdit(true);
                                            }}
                                            disabled={isActual}
                                            variant='contained'
                                        >
                                            <AddIcon />
                                            Add
                                        </Button>
                                    </span>
                                </Tooltip>
                            );
                        } else {
                            return '';
                        }
                    } else {
                        return params.rowNode.type === 'group' ? null : (
                            <Tooltip
                                title='Edit or delete an existing line of cost here'
                                placement='top'
                            >
                                <span>
                                    <MuiMenuDropdown
                                        buttonText={'Actions'}
                                        isDisabled={
                                            mode === 'read' || isApproved || isActual ? true : false
                                        }
                                        ref={gridActionMenuRef}
                                    >
                                        <MenuItem
                                            onClick={(e) => {
                                                gridActionMenuRef.current.handleCloseExternal();
                                                setIsNew(false);
                                                setSelectedRow(params.row);
                                                setOpenFundingCostEdit(true);
                                            }}
                                            disableRipple
                                        >
                                            <AttachMoneyIcon />
                                            Edit
                                        </MenuItem>
                                        <MenuItem
                                            onClick={(e) => {
                                                gridActionMenuRef.current.handleCloseExternal();
                                                setIsNew(false);
                                                setSelectedRow(params.row);
                                                setOpenDeleteConfirmation(true);
                                            }}
                                            disableRipple
                                        >
                                            <DeleteIcon />
                                            Delete
                                        </MenuItem>
                                    </MuiMenuDropdown>
                                </span>
                            </Tooltip>
                        );
                    }
                },
            });
        }
    });

    const handleClearFundingCostEntry = () => {
        setOpenFundingCostEdit(false);
        setSelectedRow(null);
        setPersonnelCost(0);
        setOtherCost(0);
        setAssetCost(0);
        setGrantsFixedCost(0);
        setContractualCost(0);
        setTotalCost(0);
        setDepartmentSelection('');
        setAgencySelection('');
        setRegionSelection('');
        setUnitSelection('');
        setSubUnitSelection('');
        setTribeSelection('');
        setFundingUnitTypeSelection('');
        setCostCenterCodeSelection('');
        setFundingUnitIDSelection('');
        setFunctionalAreaSelection('');
        setFundingSourceSelection('');
        setContributedNotesSelection('');
        setFundingSourceProgramSelection('');
        setFundingSourceCategorySelection('');
        setFundingSourceSubCategorySelection('');
    };

    const handleClearFundingAcres = () => {
        setOpenFundingAcresEdit(false);
    };

    const buildCurrentRecord = (currentRecord, isNew) => {
        const currDate = getSaveableDate(new Date());
        currentRecord[FieldNames.fundingFields.modifiedDate] = currDate;
        currentRecord[FieldNames.fundingFields.modifiedBy] =
            userInfo[FieldNames.userFields.nifcUsername];
        currentRecord[FieldNames.fundingFields.fundingSource] = fundingSourceSelection
            ? fundingSourceSelection
            : null;
        currentRecord[FieldNames.fundingFields.fundingSourceCategory] =
            fundingSourceCategorySelection ? fundingSourceCategorySelection : null;
        currentRecord[FieldNames.fundingFields.fundingSourceSubCategory] =
            fundingSourceSubCategorySelection ? fundingSourceSubCategorySelection : null;
        currentRecord[FieldNames.fundingFields.notes] = contributedNotesSelection
            ? contributedNotesSelection
            : null;
        currentRecord[FieldNames.fundingFields.fundingSourceProgram] = fundingSourceProgramSelection
            ? fundingSourceProgramSelection
            : null;
        currentRecord[FieldNames.fundingFields.fundingDepartment] = departmentSelection
            ? departmentSelection
            : null;
        currentRecord[FieldNames.fundingFields.fundingAgency] = agencySelection
            ? agencySelection
            : null;
        currentRecord[FieldNames.fundingFields.fundingRegion] = regionSelection
            ? regionSelection
            : null;
        currentRecord[FieldNames.fundingFields.fundingUnit] = unitSelection ? unitSelection : null;
        currentRecord[FieldNames.fundingFields.fundingSubUnit] = subUnitSelection
            ? subUnitSelection
            : null;
        currentRecord[FieldNames.fundingFields.fundingTribe] = tribeSelection
            ? tribeSelection
            : null;
        currentRecord[FieldNames.fundingFields.fundingUnitType] = fundingUnitTypeSelection
            ? fundingUnitTypeSelection
            : null;
        currentRecord[FieldNames.fundingFields.costCenter] = costCenterCodeSelection
            ? costCenterCodeSelection
            : 'XXXXXXXXXX';
        currentRecord[FieldNames.fundingFields.fundingUnitID] = fundingUnitIDSelection
            ? fundingUnitIDSelection
            : null;
        currentRecord[FieldNames.fundingFields.functionalArea] = functionalAreaSelection
            ? functionalAreaSelection
            : 'XXXXXXXXX.XXXXXX';
        const costCodeBuiltOut = `${costCenterCodeSelection ? costCenterCodeSelection : 'XXXXXXXXXX'}/${functionalAreaSelection ? functionalAreaSelection : 'XXXXXXXXX.XXXXXX'}/${editableFeature && editableFeature.attributes && editableFeature.attributes[FieldNames.featureFields.wbsID] ? editableFeature.attributes[FieldNames.featureFields.wbsID] : 'XXXXXXXXXXXX'}`;
        currentRecord[FieldNames.fundingFields.costCode] = costCodeBuiltOut;
        //Remove Commas
        currentRecord[FieldNames.fundingFields.personnelCost] = personnelCost
            ? removeCommas(personnelCost)
            : 0;
        currentRecord[FieldNames.fundingFields.otherCost] = otherCost ? removeCommas(otherCost) : 0;
        currentRecord[FieldNames.fundingFields.assetCost] = assetCost ? removeCommas(assetCost) : 0;
        currentRecord[FieldNames.fundingFields.grantsFixedCost] = grantsFixedCost
            ? removeCommas(grantsFixedCost)
            : 0;
        currentRecord[FieldNames.fundingFields.contractualCost] = contractualCost
            ? removeCommas(contractualCost)
            : 0;
        currentRecord[FieldNames.fundingFields.totalCost] = totalCost ? removeCommas(totalCost) : 0;
        currentRecord[FieldNames.fundingFields.isBIL] =
            fundingSourceCategorySelection &&
            fundingSourceCategorySelection.toLowerCase().includes('bil')
                ? FieldNames.yesNo.yes
                : FieldNames.yesNo.no;
        currentRecord[FieldNames.fundingFields.isBAR] =
            fundingSourceCategorySelection &&
            fundingSourceCategorySelection.toLowerCase().includes('bar')
                ? FieldNames.yesNo.yes
                : FieldNames.yesNo.no;
        currentRecord[FieldNames.fundingFields.isContributed] =
            fundingSourceProgramSelection &&
            fundingSourceProgramSelection.toLowerCase().includes('contributed')
                ? FieldNames.yesNo.yes
                : FieldNames.yesNo.no;
        currentRecord[FieldNames.fundingFields.isArchived] = FieldNames.yesNo.no;

        if (isNew) {
            currentRecord[FieldNames.fundingFields.joinID] =
                editableFeature.attributes[FieldNames.fundingFields.joinID];
            currentRecord[FieldNames.fundingFields.joinFeature] =
                editableFeature.attributes[FieldNames.fundingFields.joinFeature];
            currentRecord[FieldNames.fundingFields.id] = null;
            currentRecord[FieldNames.fundingFields.createdDate] = currDate;
            currentRecord[FieldNames.fundingFields.createdBy] =
                userInfo[FieldNames.userFields.nifcUsername];
        }
        return currentRecord;
    };

    // Formatting records for saving to the DB other than the one being actively edited.
    const formatCurrentRecord = (currentRecord) => {
        const currDate = getSaveableDate(new Date());
        currentRecord[FieldNames.fundingFields.modifiedDate] = currDate;
        currentRecord[FieldNames.fundingFields.modifiedBy] =
            userInfo[FieldNames.userFields.nifcUsername];
        currentRecord[FieldNames.fundingFields.costCenter] =
            currentRecord[FieldNames.fundingFields.costCenter] ?? 'XXXXXXXXXX';
        currentRecord[FieldNames.fundingFields.functionalArea] =
            currentRecord[FieldNames.fundingFields.functionalArea] ?? 'XXXXXXXXX.XXXXXX';
        currentRecord[FieldNames.fundingFields.costCode] =
            currentRecord[FieldNames.fundingFields.costCode] ??
            'XXXXXXXXXX/XXXXXXXXX.XXXXXX/XXXXXXXXXXXX';
        currentRecord[FieldNames.fundingFields.personnelCost] = currentRecord[
            FieldNames.fundingFields.personnelCost
        ]
            ? removeCommas(currentRecord[FieldNames.fundingFields.personnelCost])
            : 0;
        currentRecord[FieldNames.fundingFields.otherCost] = currentRecord[
            FieldNames.fundingFields.otherCost
        ]
            ? removeCommas(currentRecord[FieldNames.fundingFields.otherCost])
            : 0;
        currentRecord[FieldNames.fundingFields.assetCost] = currentRecord[
            FieldNames.fundingFields.assetCost
        ]
            ? removeCommas(currentRecord[FieldNames.fundingFields.assetCost])
            : 0;
        currentRecord[FieldNames.fundingFields.grantsFixedCost] = currentRecord[
            FieldNames.fundingFields.grantsFixedCost
        ]
            ? removeCommas(currentRecord[FieldNames.fundingFields.grantsFixedCost])
            : 0;
        currentRecord[FieldNames.fundingFields.contractualCost] = currentRecord[
            FieldNames.fundingFields.contractualCost
        ]
            ? removeCommas(currentRecord[FieldNames.fundingFields.contractualCost])
            : 0;
        currentRecord[FieldNames.fundingFields.totalCost] = currentRecord[
            FieldNames.fundingFields.totalCost
        ]
            ? removeCommas(currentRecord[FieldNames.fundingFields.totalCost])
            : 0;
        return currentRecord;
    };

    const handleCancelDelete = () => {
        setOpenDeleteConfirmation(false);
        handleClearFundingCostEntry();
    };

    const handleDelete = async () => {
        try {
            dispatch(setShowGlobalLoader(true));
            setOpenDeleteConfirmation(false);
            setIsFundingRowSaving(true);
            const otherRecords = rows.filter(
                (x) => x[FieldNames.fundingFields.id] !== selectedRow[FieldNames.fundingFields.id]
            );

            const deletionResponse = await FundingService.deleteFundingEntries([
                selectedRow[FieldNames.fundingFields.objectID],
            ]);

            const formattedRemainingRecords = otherRecords.map((record) => {
                return formatCurrentRecord(record);
            });
            let updatedGridRows = CalculateDefaultFundingAcres(
                formattedRemainingRecords,
                editableFeature.attributes[FieldNames.featureFields.calculatedArea]
            );
            if (updatedGridRows?.length > 0) {
                const updateResp = await FundingService.updateFundingEntries(updatedGridRows);
            }

            setSnackbarMessage('Deletion of a Funding Cost Entry was successful');
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
            saveFeatureFundingCostValues(updatedGridRows, `Funding row was deleted.`);
        } catch (error) {
            ErrorService.addErrorLogEntry(userInfo[FieldNames.userFields.nifcUsername], error);
            setSnackbarMessage('There was an error updating the funding rows');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            // Query anew in case we failed partway
            const fundingRows = await getFundingCostByFeatureID();
            saveFeatureFundingCostValues(fundingRows);
        } finally {
            dispatch(setShowGlobalLoader(false));
            setIsFundingRowSaving(false);
            handleClearFundingCostEntry();
        }
    };

    const saveFeatureFundingCostValues = async (currentRows, saveMessage) => {
        //Save all values in funding fields as a string with commas between
        //Save all values in cost fields as a total of all values for that field
        if (editableFeature?.attributes && !isActual) {
            setIsFundingFeatureSaving(true);
            let tempEditableFeature = Object.assign({}, editableFeature);
            let oldUnitType = editableFeature.attributes[FieldNames.featureFields.fundingUnitType];
            let oldFeatureIsPoint =
                editableFeature.attributes[FieldNames.featureFields.isPoint] === 'Yes';
            let deptArray = [];
            let agencyArray = [];
            let regionArray = [];
            let unitArray = [];
            let subUnitArray = [];
            let tribeArray = [];
            let unitTypeArray = [];
            let fundingUnitIDArray = [];
            let costCenterArray = [];
            let functionalAreaArray = [];
            let costCenterCodeArray = [];
            let fundingSourceArray = [];
            let fundingSourceProgramArray = [];
            let fundingSourceCategoryArray = [];
            let fundingSourceSubCategoryArray = [];
            let tempIsBil = isBil;
            let tempIsBar = isBar;
            let assetCostSum = 0;
            let contractualCostSum = 0;
            let grantsFixedCostSum = 0;
            let otherCostSum = 0;
            let personnelCostSum = 0;
            let totalCostSum = 0;
            let totalBilSum = 0;
            let isRowBil = false;
            let isRowBar = false;
            if (currentRows.length > 0) {
                currentRows.forEach((row) => {
                    if (
                        row[FieldNames.fundingFields.isBIL] === FieldNames.yesNo.yes ||
                        (row[FieldNames.fundingFields.fundingSourceCategory] &&
                            row[FieldNames.fundingFields.fundingSourceCategory]
                                .toLowerCase()
                                .includes('bil'))
                    ) {
                        isRowBil = true;
                    }

                    if (
                        row[FieldNames.fundingFields.isBAR] === FieldNames.yesNo.yes ||
                        (row[FieldNames.fundingFields.fundingSourceCategory] &&
                            row[FieldNames.fundingFields.fundingSourceCategory]
                                .toLowerCase()
                                .includes('bar'))
                    ) {
                        isRowBar = true;
                    }

                    if (
                        row[FieldNames.fundingFields.fundingDepartment] &&
                        !deptArray.includes(row[FieldNames.fundingFields.fundingDepartment])
                    ) {
                        deptArray.push(row[FieldNames.fundingFields.fundingDepartment]);
                    }
                    if (
                        row[FieldNames.fundingFields.fundingAgency] &&
                        !agencyArray.includes(row[FieldNames.fundingFields.fundingAgency])
                    ) {
                        agencyArray.push(row[FieldNames.fundingFields.fundingAgency]);
                    }
                    if (
                        row[FieldNames.fundingFields.fundingRegion] &&
                        !regionArray.includes(row[FieldNames.fundingFields.fundingRegion])
                    ) {
                        regionArray.push(row[FieldNames.fundingFields.fundingRegion]);
                    }
                    if (
                        row[FieldNames.fundingFields.fundingUnit] &&
                        !unitArray.includes(row[FieldNames.fundingFields.fundingUnit])
                    ) {
                        unitArray.push(row[FieldNames.fundingFields.fundingUnit]);
                    }
                    if (
                        row[FieldNames.fundingFields.fundingUnitType] &&
                        !unitTypeArray.includes(row[FieldNames.fundingFields.fundingUnitType])
                    ) {
                        unitTypeArray.push(row[FieldNames.fundingFields.fundingUnitType]);
                    }
                    if (
                        row[FieldNames.fundingFields.fundingUnitID] &&
                        !fundingUnitIDArray.includes(row[FieldNames.fundingFields.fundingUnitID])
                    ) {
                        fundingUnitIDArray.push(row[FieldNames.fundingFields.fundingUnitID]);
                    }
                    if (
                        row[FieldNames.fundingFields.fundingSubUnit] &&
                        !subUnitArray.includes(row[FieldNames.fundingFields.fundingSubUnit])
                    ) {
                        subUnitArray.push(row[FieldNames.fundingFields.fundingSubUnit]);
                    }
                    if (
                        row[FieldNames.fundingFields.fundingTribe] &&
                        !tribeArray.includes(row[FieldNames.fundingFields.fundingTribe])
                    ) {
                        tribeArray.push(row[FieldNames.fundingFields.fundingTribe]);
                    }
                    if (
                        row[FieldNames.fundingFields.costCenter] &&
                        !costCenterArray.includes(row[FieldNames.fundingFields.costCenter])
                    ) {
                        costCenterArray.push(row[FieldNames.fundingFields.costCenter]);
                    }
                    if (
                        row[FieldNames.fundingFields.functionalArea] &&
                        !functionalAreaArray.includes(row[FieldNames.fundingFields.functionalArea])
                    ) {
                        functionalAreaArray.push(row[FieldNames.fundingFields.functionalArea]);
                    }
                    if (
                        row[FieldNames.fundingFields.costCode] &&
                        !costCenterCodeArray.includes(row[FieldNames.fundingFields.costCode])
                    ) {
                        costCenterCodeArray.push(row[FieldNames.fundingFields.costCode]);
                    }
                    if (
                        row[FieldNames.fundingFields.fundingSource] &&
                        !fundingSourceArray.includes(row[FieldNames.fundingFields.fundingSource])
                    ) {
                        fundingSourceArray.push(row[FieldNames.fundingFields.fundingSource]);
                    }
                    if (
                        row[FieldNames.fundingFields.fundingSourceProgram] &&
                        !fundingSourceProgramArray.includes(
                            row[FieldNames.fundingFields.fundingSourceProgram]
                        )
                    ) {
                        fundingSourceProgramArray.push(
                            row[FieldNames.fundingFields.fundingSourceProgram]
                        );
                    }
                    if (
                        row[FieldNames.fundingFields.fundingSourceCategory] &&
                        !fundingSourceCategoryArray.includes(
                            row[FieldNames.fundingFields.fundingSourceCategory]
                        )
                    ) {
                        fundingSourceCategoryArray.push(
                            row[FieldNames.fundingFields.fundingSourceCategory]
                        );
                    }
                    if (
                        row[FieldNames.fundingFields.fundingSourceSubCategory] &&
                        !fundingSourceSubCategoryArray.includes(
                            row[FieldNames.fundingFields.fundingSourceSubCategory]
                        )
                    ) {
                        fundingSourceSubCategoryArray.push(
                            row[FieldNames.fundingFields.fundingSourceSubCategory]
                        );
                    }

                    if (row[FieldNames.fundingFields.assetCost]) {
                        assetCostSum += removeCommas(row[FieldNames.fundingFields.assetCost]);
                    }
                    if (row[FieldNames.fundingFields.contractualCost]) {
                        contractualCostSum += removeCommas(
                            row[FieldNames.fundingFields.contractualCost]
                        );
                    }
                    if (row[FieldNames.fundingFields.grantsFixedCost]) {
                        grantsFixedCostSum += removeCommas(
                            row[FieldNames.fundingFields.grantsFixedCost]
                        );
                    }
                    if (row[FieldNames.fundingFields.otherCost]) {
                        otherCostSum += removeCommas(row[FieldNames.fundingFields.otherCost]);
                    }
                    if (row[FieldNames.fundingFields.personnelCost]) {
                        personnelCostSum += removeCommas(
                            row[FieldNames.fundingFields.personnelCost]
                        );
                    }
                    if (row[FieldNames.fundingFields.totalCost]) {
                        totalCostSum += removeCommas(row[FieldNames.fundingFields.totalCost]);
                    }
                    if (isRowBil) {
                        // Separate from totalCostSum to avoid Contributed funding
                        totalBilSum += removeCommas(row[FieldNames.fundingFields.totalCost]);
                    }
                });

                if (isRowBil) {
                    setIsBil(true);
                    tempIsBil = true;
                }

                if (isRowBar) {
                    setIsBar(true);
                    tempIsBar = true;
                }
            } else {
                if (tempIsBil) {
                    setIsBil(false);
                    tempIsBil = false;
                }

                if (tempIsBar) {
                    setIsBar(false);
                    tempIsBar = false;
                }
            }
            if (agencyArray.length > 0) {
                let joinedAgencyStr = agencyArray.join('|');
                if (
                    tempEditableFeature.attributes[FieldNames.featureFields.fundingAgency] &&
                    !tempEditableFeature.attributes[
                        FieldNames.featureFields.fundingAgency
                    ].includes(FieldNames.agencies.blm) &&
                    joinedAgencyStr.includes(FieldNames.agencies.blm)
                ) {
                    // We went from non-blm to at least one blm cost being added
                    tempEditableFeature.attributes[FieldNames.featureFields.wbsID] = 'XXXXXXXXXXXX';
                } else if (
                    tempEditableFeature.attributes[FieldNames.featureFields.fundingAgency] &&
                    tempEditableFeature.attributes[FieldNames.featureFields.fundingAgency].includes(
                        FieldNames.agencies.blm
                    ) &&
                    !joinedAgencyStr.includes(FieldNames.agencies.blm)
                ) {
                    const baseValue =
                        tempEditableFeature.attributes[FieldNames.featureFields.wbsID];
                    const prefixRemoved = baseValue.replace(FieldNames.wbsPrefixes.blm, '');
                    const suffixRemoved = prefixRemoved.slice(0, -4);
                    // We went from blm funding to no blm funding
                    tempEditableFeature.attributes[FieldNames.featureFields.wbsID] = suffixRemoved;
                }
                if (
                    tempEditableFeature.attributes[FieldNames.featureFields.isRTRL] == null &&
                    joinedAgencyStr.includes(FieldNames.agencies.bia)
                ) {
                    // Changing 'to' a funding agency of BIA. Default IsRTRL to no.
                    tempEditableFeature.attributes[FieldNames.featureFields.isRTRL] =
                        FieldNames.yesNo.no;
                }
            }
            const currDate = getSaveableDate(new Date());
            const totalRegions = regionArray.length > 0 ? regionArray.join('|') : null;
            const totalDepartments = deptArray.length > 0 ? deptArray.join('|') : null;
            const totalAgencies = agencyArray.length > 0 ? agencyArray.join('|') : null;
            const totalUnits = unitArray.length > 0 ? unitArray.join('|') : null;
            const totalSubUnits = subUnitArray.length > 0 ? subUnitArray.join('|') : null;
            const totalTribes = tribeArray.length > 0 ? tribeArray.join('|') : null;
            const totalFundingSources =
                fundingSourceArray.length > 0 ? fundingSourceArray.join('|') : null;
            const totalFundingSourcePrograms =
                fundingSourceProgramArray.length > 0 ? fundingSourceProgramArray.join('|') : null;
            const totalFundingSourceCategories =
                fundingSourceCategoryArray.length > 0 ? fundingSourceCategoryArray.join('|') : null;
            const totalFundingSourceSubCategories =
                fundingSourceSubCategoryArray.length > 0
                    ? fundingSourceSubCategoryArray.join('|')
                    : null;
            const totalUnitTypes = unitTypeArray.length > 0 ? unitTypeArray.join('|') : null;
            const totalUnitIDs =
                fundingUnitIDArray.length > 0 ? fundingUnitIDArray.join('|') : null;
            const totalFunctionalAreas =
                functionalAreaArray.length > 0 ? functionalAreaArray.join('|') : null;
            const totalCostCenters = costCenterArray.length > 0 ? costCenterArray.join('|') : null;
            const totalCostCodes =
                costCenterCodeArray.length > 0 ? costCenterCodeArray.join('|') : null;

            tempEditableFeature.attributes[FieldNames.featureFields.fundingDepartment] =
                totalDepartments;
            tempEditableFeature.attributes[FieldNames.featureFields.fundingAgency] = totalAgencies;
            tempEditableFeature.attributes[FieldNames.featureFields.fundingRegion] = totalRegions;
            tempEditableFeature.attributes[FieldNames.featureFields.fundingUnit] = totalUnits;
            tempEditableFeature.attributes[FieldNames.featureFields.fundingSubUnit] = totalSubUnits;
            tempEditableFeature.attributes[FieldNames.featureFields.fundingTribe] = totalTribes;
            tempEditableFeature.attributes[FieldNames.featureFields.fundingUnitType] =
                totalUnitTypes;
            tempEditableFeature.attributes[FieldNames.featureFields.fundingUnitID] = totalUnitIDs;
            tempEditableFeature.attributes[FieldNames.featureFields.estimatedAssetCost] =
                assetCostSum;
            tempEditableFeature.attributes[FieldNames.featureFields.estimatedContractualCost] =
                contractualCostSum;
            tempEditableFeature.attributes[FieldNames.featureFields.estimatedGrantsFixedCost] =
                grantsFixedCostSum;
            tempEditableFeature.attributes[FieldNames.featureFields.estimatedPersonnelCost] =
                personnelCostSum;
            tempEditableFeature.attributes[FieldNames.featureFields.estimatedOtherCost] =
                otherCostSum;
            tempEditableFeature.attributes[FieldNames.featureFields.estimatedTotalCost] =
                totalCostSum;
            tempEditableFeature.attributes[FieldNames.featureFields.modifiedBy] =
                userInfo[FieldNames.userFields.nifcUsername];
            tempEditableFeature.attributes[FieldNames.featureFields.lastModifiedDate] = currDate;
            tempEditableFeature.attributes[FieldNames.featureFields.functionalArea] =
                totalFunctionalAreas;
            tempEditableFeature.attributes[FieldNames.featureFields.costCenter] = totalCostCenters;
            tempEditableFeature.attributes[FieldNames.featureFields.costCode] = totalCostCodes;
            tempEditableFeature.attributes[FieldNames.featureFields.isBIL] = tempIsBil
                ? FieldNames.yesNo.yes
                : FieldNames.yesNo.no;
            tempEditableFeature.attributes[FieldNames.featureFields.isBAR] = tempIsBar
                ? FieldNames.yesNo.yes
                : FieldNames.yesNo.no;
            tempEditableFeature.attributes[FieldNames.featureFields.bilFunding] = tempIsBil
                ? totalBilSum
                : 0;
            tempEditableFeature.attributes[FieldNames.featureFields.fundingSource] =
                totalFundingSources;
            tempEditableFeature.attributes[FieldNames.featureFields.fundingSourceProgram] =
                totalFundingSourcePrograms;
            tempEditableFeature.attributes[FieldNames.featureFields.fundingSourceCategory] =
                totalFundingSourceCategories;
            tempEditableFeature.attributes[FieldNames.featureFields.fundingSourceSubCategory] =
                totalFundingSourceSubCategories;
            tempEditableFeature.attributes[FieldNames.featureFields.isFundingAcresManual] =
                FieldNames.yesNo.no;
            if (tempEditableFeature && !isTreatment) {
                let fields = FieldNames.featureFields;
                const fundUnitTypeProp = tempEditableFeature.attributes.hasOwnProperty(
                    fields.fundingUnitType
                );
                if (fundUnitTypeProp) {
                    // FUELS-504: Change was made to funding units, check for point management activity
                    checkPointManagementActivityChange(
                        tempEditableFeature,
                        oldFeatureIsPoint,
                        oldUnitType,
                        fundUnitTypeProp
                            ? tempEditableFeature.attributes[fields.fundingUnitType]
                                ? tempEditableFeature.attributes[fields.fundingUnitType]
                                : ''
                            : oldUnitType
                    );
                }
            }

            const missingFieldsCount = getAllMissingFieldsCount(
                tempEditableFeature,
                isTreatment,
                userInfo,
                false,
                null
            );
            const newStatus = getStatus(
                editableFeature,
                tempEditableFeature.attributes[FieldNames.featureFields.hasActual],
                false,
                missingFieldsCount
            );
            tempEditableFeature.attributes[FieldNames.featureFields.status] = newStatus;

            emitCustomEvent(Events.UpdateRequiredFields, {
                feature: tempEditableFeature,
                isTreatment: isTreatment,
            });
            try {
                const featureUpdateResp = await FeatureService.updateFeature(
                    tempEditableFeature.attributes
                );
                const feature = await window.api.getConcatenatedActionByObjectID(
                    tempEditableFeature.attributes?.[FieldNames.featureFields.objectID]
                );

                const newEditableFeature = feature;
                newEditableFeature.geometry = tempEditableFeature.geometry;
                newEditableFeature.attributes[FieldNames.featureFields.joinObjectID] =
                    tempEditableFeature.attributes[FieldNames.featureFields.joinObjectID];
                setEditableFeature(newEditableFeature);
                setBaseFeatureAttributes(_.cloneDeep(newEditableFeature.attributes));

                try {
                    const fundingChange = saveMessage ? `${saveMessage} ` : '';
                    const changeString =
                        getLogForFieldUpdatesOnExistingFeature(featureUnsavedFields);
                    const finalChangeString = `${fundingChange}${changeString}`;
                    const auditResponse = await AuditLogService.addAuditLogEntry(
                        feature.attributes,
                        feature.attributes[FieldNames.featureFields.joinID],
                        feature.attributes[FieldNames.featureFields.joinFeature],
                        userInfo[FieldNames.userFields.nifcUsername],
                        finalChangeString
                    );
                } catch (error) {
                    ErrorService.addErrorLogEntry(
                        rootServiceUrl,
                        config,
                        nifcToken,
                        userInfo[FieldNames.userFields.nifcUsername],
                        error
                    );
                }
            } catch (error) {
                ErrorService.addErrorLogEntry(
                    rootServiceUrl,
                    config,
                    nifcToken,
                    userInfo[FieldNames.userFields.nifcUsername],
                    error
                );
            } finally {
                setIsFundingFeatureSaving(false);
            }
        } else if (editableFeature?.attributes) {
            let tempEditableFeature = Object.assign({}, editableFeature);
            tempEditableFeature.attributes[FieldNames.featureFields.isFundingAcresManual] =
                FieldNames.yesNo.no;
            setEditableFeature(tempEditableFeature);
            emitCustomEvent(Events.UpdateRequiredFields, {
                feature: tempEditableFeature,
                isTreatment: isTreatment,
            });
        }
    };

    const rederiveFeatureCallback = (
        feature,
        isTreatment,
        isFeatureActuals,
        isPoint,
        wfftsAttributes,
        legacyAttributes
    ) => {
        const resetAcreage = false;
        rederiveFeatureInfo(
            feature,
            isTreatment,
            isFeatureActuals,
            isPoint,
            wfftsAttributes,
            legacyAttributes,
            resetAcreage
        );
    };

    const checkPointManagementActivityChange = (feature, isPoint, oldUnitType, newUnitType) => {
        // Note: This logic differs from the equivalent within DetailsContainer in that we should always update the fields if newUnitType is applicable
        //        to catch funding field updates and apply them to the location fields. Additionally, only unitType can change from this workflow,
        //        so checking category is unnecessary.
        if (isPoint) {
            let unitTypeMatchesCriteria = false;
            let unitTypeChangedFromCriteria = false;
            if (
                (oldUnitType === FieldNames.unitTypes.national ||
                    oldUnitType === FieldNames.unitTypes.regional) &&
                (!newUnitType ||
                    !(
                        newUnitType === FieldNames.unitTypes.national ||
                        newUnitType === FieldNames.unitTypes.regional
                    ))
            ) {
                unitTypeChangedFromCriteria = true;
            }
            if (
                newUnitType === FieldNames.unitTypes.national ||
                newUnitType === FieldNames.unitTypes.regional
            ) {
                unitTypeMatchesCriteria = true;
            }

            if (unitTypeChangedFromCriteria) {
                // Feature is being altered from the conditions. Feature is always a point per original FUELS-504 conditions
                rederiveFeatureCallback(feature, isTreatment, false, true, null, null);
            } else if (unitTypeMatchesCriteria) {
                // Feature is being altered to the conditions. Set the location fields to the funding fields.
                setLocationFieldsToFundingFields(feature);
                if (feature.attributes) {
                    feature.attributes[FieldNames.featureFields.locationDepartmentLocked] =
                        FieldNames.yesNo.no;
                    feature.attributes[FieldNames.featureFields.locationAgencyLocked] =
                        FieldNames.yesNo.no;
                    feature.attributes[FieldNames.featureFields.locationRegionLocked] =
                        FieldNames.yesNo.no;
                    feature.attributes[FieldNames.featureFields.locationUnitLocked] =
                        FieldNames.yesNo.no;
                }
            }
        }
    };

    const setLocationFieldsToFundingFields = (feature) => {
        // Check if feature is activity/point, program management category, and unit type of national/regional
        if (feature && feature.attributes) {
            // Note: The departmentValue through unitValue state variables are not updated as of this point. Use the feature attributes if possible.
            var fundingDepVal = feature.attributes[FieldNames.featureFields.fundingDepartment]
                ? feature.attributes[FieldNames.featureFields.fundingDepartment].includes('|')
                    ? 'NA Multiple Funding Units'
                    : feature.attributes[FieldNames.featureFields.fundingDepartment]
                : '';
            var fundingAgeVal = feature.attributes[FieldNames.featureFields.fundingAgency]
                ? feature.attributes[FieldNames.featureFields.fundingAgency].includes('|')
                    ? 'NA Multiple Funding Units'
                    : feature.attributes[FieldNames.featureFields.fundingAgency]
                : '';
            var fundingRegVal = feature.attributes[FieldNames.featureFields.fundingRegion]
                ? feature.attributes[FieldNames.featureFields.fundingRegion].includes('|')
                    ? 'NA Multiple Funding Units'
                    : feature.attributes[FieldNames.featureFields.fundingRegion]
                : '';
            var fundingUnitVal = feature.attributes[FieldNames.featureFields.fundingUnit]
                ? feature.attributes[FieldNames.featureFields.fundingUnit].includes('|')
                    ? 'NA Multiple Funding Units'
                    : feature.attributes[FieldNames.featureFields.fundingUnit]
                : '';

            feature.attributes[FieldNames.featureFields.department] = fundingDepVal;
            feature.attributes[FieldNames.featureFields.agency] = fundingAgeVal;
            feature.attributes[FieldNames.featureFields.region] = fundingRegVal;
            feature.attributes[FieldNames.featureFields.unit] = fundingUnitVal;
            if (setLocationDepartmentValue) {
                setLocationDepartmentValue(fundingDepVal);
            }
            if (setLocationAgencyValue) {
                setLocationAgencyValue(fundingAgeVal);
            }
            if (setLocationRegionValue) {
                setLocationRegionValue(fundingRegVal);
            }
            if (setLocationUnitValue) {
                setLocationUnitValue(fundingUnitVal);
            }
        }
    };

    const handleSaveFundingCostEntry = async (e, isNew) => {
        try {
            dispatch(setShowGlobalLoader(true));
            let originalRecord = {};
            let updatedRecord = {};
            setIsFundingRowSaving(true);
            let tempUpdatedFundingRecords = rows.map((record) => {
                if (
                    !isNew &&
                    record &&
                    selectedRow &&
                    record[FieldNames.fundingFields.id] === selectedRow[FieldNames.fundingFields.id]
                ) {
                    originalRecord = _.cloneDeep(record);
                    return buildCurrentRecord(record, isNew);
                }
                return formatCurrentRecord(record);
            });
            if (isNew) {
                let currentRecord = {};
                currentRecord = buildCurrentRecord(currentRecord, isNew);
                tempUpdatedFundingRecords = [...tempUpdatedFundingRecords, currentRecord];
                updatedRecord = currentRecord;
            } else {
                let recordBeingUpdated = rows.filter(
                    (record) =>
                        record[FieldNames.fundingFields.id] ===
                        selectedRow?.[FieldNames.fundingFields.id]
                )?.[0];
                updatedRecord = recordBeingUpdated;
            }

            // always set area to 0 for non-polygons or point activities
            const area =
                editableFeature?.attributes[FieldNames.featureFields.joinFeature] !== 'Polygon' ||
                editableFeature?.attributes[FieldNames.featureFields.isPoint] ===
                    FieldNames.yesNo.yes
                    ? 0
                    : editableFeature.attributes[FieldNames.featureFields.calculatedArea];
            let updatedGridRows = CalculateDefaultFundingAcres(tempUpdatedFundingRecords, area);
            let newGridRows = updatedGridRows.filter(
                (row) => !row[FieldNames.featureFields.objectID]
            );
            let existingGridRows = updatedGridRows.filter(
                (row) => row[FieldNames.featureFields.objectID] != null
            );

            if (newGridRows.length > 0) {
                let addResponse = await FundingService.addFundingEntries(newGridRows);
            }

            if (existingGridRows.length > 0) {
                const updateReponse = await FundingService.updateFundingEntries(existingGridRows);
            }

            handleClearFundingCostEntry();
            let changedFieldArr = isNew
                ? null
                : getChangedValuesFromObject(originalRecord, updatedRecord, false);
            // Remove calculated fields that aren't stored in the database.
            let filteredChangeFields = isNew
                ? null
                : changedFieldArr?.filter((x) => x !== FieldNames.fundingFields.isBAR);
            let changedFieldStr = isNew ? null : filteredChangeFields?.join(', ');
            const saveString = isNew
                ? 'Cost row added.'
                : `The following fields have been modified on cost row ID=${updatedRecord[FieldNames.featureFields.id]}: ${changedFieldStr}`;
            saveFeatureFundingCostValues(updatedGridRows, saveString);
        } catch (error) {
            ErrorService.addErrorLogEntry(userInfo[FieldNames.userFields.nifcUsername], error);
            setSnackbarSeverity('error');
            setSnackbarMessage('Error when updating Cost Rows');
            setSnackbarOpen(true);
            // Query anew in case we failed partway through
            const fundingRows = await getFundingCostByFeatureID();
            saveFeatureFundingCostValues(fundingRows);
        } finally {
            dispatch(setShowGlobalLoader(false));
            setIsFundingRowSaving(false);
        }
    };

    const resetFundingAcres = async () => {
        try {
            dispatch(setShowGlobalLoader(true));
            let tempUpdatedFundingRecords = rows.map((record) => {
                return formatCurrentRecord(record);
            });
            const area =
                editableFeature?.attributes[FieldNames.featureFields.joinFeature] !== 'Polygon' ||
                editableFeature?.attributes[FieldNames.featureFields.isPoint] ===
                    FieldNames.yesNo.yes
                    ? 0
                    : editableFeature.attributes[FieldNames.featureFields.calculatedArea];
            let updatedGridRows = CalculateDefaultFundingAcres(tempUpdatedFundingRecords, area);
            if (updatedGridRows?.length > 0) {
                const updateResp = await FundingService.updateFundingEntries(updatedGridRows);
            }

            setSnackbarMessage('Successful reset funding acreage distribution');
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
            saveFeatureFundingCostValues(updatedGridRows, `Funding row acreage reset`);
        } catch (e) {
            ErrorService.addErrorLogEntry(userInfo[FieldNames.userFields.nifcUsername], e);
            setSnackbarMessage('There was an error updating the funding row acreage');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        } finally {
            dispatch(setShowGlobalLoader(false));
        }
    };

    const exportOptions = {
        getRowsToExport: () => gridFilteredSortedRowIdsSelector(apiRef),
    };

    function CustomExportContainer(props) {
        return (
            <GridToolbarExportContainer {...props}>
                <GridCsvExportMenuItem
                    options={exportOptions}
                    onClick={() => apiRef.current.exportDataAsCsv(exportOptions)}
                />
                <GridExcelExportMenuItem
                    options={exportOptions}
                    onClick={() => apiRef.current.exportDataAsExcel(exportOptions)}
                />
            </GridToolbarExportContainer>
        );
    }

    function CustomCarryoverGridToolbar(props) {
        return (
            <GridToolbarContainer>
                <GridToolbarColumnsButton />
                <GridToolbarFilterButton />
                <GridToolbarDensitySelector />
                <CustomExportContainer />
            </GridToolbarContainer>
        );
    }

    const addFundingTooltipText = 'All required fields must be filled out prior to saving';

    return (
        <Box
            sx={{
                height: '100%',
                width: '100%',
                '& .actions': {
                    color: 'text.secondary',
                },
                '& .textPrimary': {
                    color: 'text.primary',
                },
            }}
        >
            <Grid container spacing={2}>
                {mode === 'edit' && !isApproved ? (
                    <Grid size={0.7}>
                        <Tooltip
                            title='Add a new line of cost here'
                            arrow
                            placement='top'
                            slotProps={{
                                popper: {
                                    modifiers: [
                                        {
                                            name: 'offset',
                                            options: {
                                                offset: [0, -6],
                                            },
                                        },
                                    ],
                                },
                            }}
                        >
                            <span>
                                <Button
                                    onClick={(e) => {
                                        setIsNew(true);
                                        setSelectedRow(null);
                                        setOpenFundingCostEdit(true);
                                    }}
                                    disabled={isActual}
                                    sx={{ margin: '20px 0px 7px 2px' }}
                                    variant='contained'
                                >
                                    <AddIcon />
                                    Add
                                </Button>
                            </span>
                        </Tooltip>
                    </Grid>
                ) : (
                    <Grid size={0.7} />
                )}
                {mode === 'edit' && !isApproved && rows && rows.length > 1 && (
                    <>
                        <Grid size={1.4}>
                            <Tooltip
                                title='Manage acreage for existing lines of cost here'
                                arrow
                                placement='top'
                                slotProps={{
                                    popper: {
                                        modifiers: [
                                            {
                                                name: 'offset',
                                                options: {
                                                    offset: [0, -6],
                                                },
                                            },
                                        ],
                                    },
                                }}
                            >
                                <span>
                                    <Button
                                        onClick={(e) => {
                                            setOpenFundingAcresEdit(true);
                                        }}
                                        sx={{
                                            margin: '20px 0px 7px 2px',
                                            width: 'auto',
                                            whiteSpace: 'nowrap',
                                        }}
                                        variant='contained'
                                        disabled={
                                            isApproved ||
                                            editableFeature.attributes[
                                                FieldNames.featureFields.joinFeature
                                            ] !== FieldNames.joinFeatureValues.polygon ||
                                            editableFeature.attributes[
                                                FieldNames.featureFields.isPoint
                                            ] === FieldNames.yesNo.yes
                                        }
                                    >
                                        Manage Acres
                                    </Button>
                                </span>
                            </Tooltip>
                        </Grid>
                        <Grid size={8}>
                            <Stack
                                direction={'row'}
                                sx={{
                                    backgroundColor: '#D3D3D3',
                                    margin: '20px 0px 7px 2px',
                                }}
                            >
                                <HelpIcon
                                    fontSize='small'
                                    color='primary'
                                    style={{ paddingRight: '5px' }}
                                />
                                <Typography fontSize={12}>
                                    NOTE: When you make changes to cost lines for a
                                    treatment/activity, the acres per cost line will be reset to
                                    default values (proportions of cost/acres). Please review and
                                    ensure the proportions make sense for the treatment/activity,
                                    and manually update if desired.
                                </Typography>
                            </Stack>
                        </Grid>
                    </>
                )}
            </Grid>
            {mode === 'edit' && !isApproved && rows && rows.length > 0 && (
                <Grid container spacing={2} sx={{ marginBottom: '5px' }}>
                    <Grid size={0.7} />
                    <Grid size={1.4}>
                        <>
                            <Tooltip
                                title='Reset distribution of acres between cost lines'
                                arrow
                                slotProps={{
                                    popper: {
                                        modifiers: [
                                            {
                                                name: 'offset',
                                                options: {
                                                    offset: [0, 6],
                                                },
                                            },
                                        ],
                                    },
                                }}
                            >
                                <span>
                                    <Button
                                        onClick={(e) => {
                                            setResetAcresPopupOpen(true);
                                        }}
                                        sx={{
                                            marginLeft: '2px',
                                            width: 'auto',
                                            whiteSpace: 'nowrap',
                                        }}
                                        variant='contained'
                                        disabled={
                                            isApproved ||
                                            editableFeature.attributes[
                                                FieldNames.featureFields.joinFeature
                                            ] !== FieldNames.joinFeatureValues.polygon ||
                                            editableFeature.attributes[
                                                FieldNames.featureFields.isPoint
                                            ] === FieldNames.yesNo.yes
                                        }
                                    >
                                        Reset Acres
                                    </Button>
                                </span>
                            </Tooltip>
                        </>
                    </Grid>
                    <Grid size={8}>
                        <Stack
                            direction={'row'}
                            sx={{
                                backgroundColor: '#D3D3D3',
                                marginLeft: '2px',
                                marginTop: '7.5px',
                            }}
                        >
                            <HelpIcon
                                fontSize='small'
                                color='primary'
                                style={{ paddingRight: '5px' }}
                            />
                            <Typography fontSize={12}>
                                Note: This will reset the acres to the default based on a
                                calculation that sets the acreage to be proportionate to the
                                percentage of cost for each line.
                            </Typography>
                        </Stack>
                    </Grid>
                </Grid>
            )}
            <DataGridPremium
                apiRef={apiRef}
                loading={isFundingCostLoading}
                rows={rows}
                columns={columns}
                getRowId={(row) =>
                    row[FieldNames.fundingFields.id] ? row[FieldNames.fundingFields.id] : randomId()
                }
                editMode='row'
                initialState={{
                    aggregation: {
                        model: {
                            [FieldNames.fundingFields.personnelCost]: 'sum',
                            [FieldNames.fundingFields.assetCost]: 'sum',
                            [FieldNames.fundingFields.otherCost]: 'sum',
                            [FieldNames.fundingFields.contractualCost]: 'sum',
                            [FieldNames.fundingFields.grantsFixedCost]: 'sum',
                            [FieldNames.fundingFields.totalCost]: 'sum',
                        },
                    },
                    columns: {
                        columnVisibilityModel: {
                            featureObjectId: false,
                            featureId: false,
                            FundingSource: false,
                        },
                    },
                }}
                rowModesModel={rowModesModel}
                onRowModesModelChange={handleRowModesModelChange}
                experimentalFeatures={{ aggregation: true }}
                showToolbar
                slots={{
                    toolbar: CustomCarryoverGridToolbar,
                    noRowsOverlay: () => (
                        <Stack height='100%' alignItems='center' justifyContent='center'>
                            {mode === 'read' || isApproved
                                ? 'View Mode, return to card to Manage Costs'
                                : 'No Rows - Click Add to Create a New Row'}
                        </Stack>
                    ),
                }}
            />
            <InFormGenericModal
                openFlag={openFundingCostEdit}
                closeMethod={handleClearFundingCostEntry}
                titleText={'Edit Funding & Costs'}
                displayButton={false}
            >
                <Box
                    style={{
                        maxHeight: '75vh',
                        overflow: 'auto',
                        maxWidth: '50vw',
                        minWidth: '50vw',
                    }}
                >
                    <Paper elevation={6} style={{ padding: 20, margin: 20 }}>
                        <h2>Update Funding</h2>
                        <RecordFundingSourceAssignment
                            userInfo={userInfo}
                            selectedRow={selectedRow}
                            setFunctionalAreaSelection={setFunctionalAreaSelection}
                            setFundingSourceSelection={setFundingSourceSelection}
                            fundingSourceProgramSelection={fundingSourceProgramSelection}
                            setFundingSourceProgramSelection={setFundingSourceProgramSelection}
                            fundingSourceCategorySelection={fundingSourceCategorySelection}
                            setFundingSourceCategorySelection={setFundingSourceCategorySelection}
                            fundingSourceSubCategorySelection={fundingSourceSubCategorySelection}
                            setFundingSourceSubCategorySelection={
                                setFundingSourceSubCategorySelection
                            }
                            contributedNotesSelection={contributedNotesSelection}
                            setContributedNotesSelection={setContributedNotesSelection}
                            rows={rows}
                            setIsBil={setIsBil}
                            requiredFields={requiredFields}
                            isTreatment={isTreatment}
                            isActual={isActual}
                            isPoint={isPoint}
                            setIsBar={setIsBar}
                            editableFeature={editableFeature}
                            setFundingSourceValid={setFundingSourceValid}
                        />
                        <RecordFundingAssignment
                            userInfo={userInfo}
                            selectedRow={selectedRow}
                            departmentSelection={departmentSelection}
                            setDepartmentSelection={setDepartmentSelection}
                            agencySelection={agencySelection}
                            setAgencySelection={setAgencySelection}
                            regionSelection={regionSelection}
                            setRegionSelection={setRegionSelection}
                            unitSelection={unitSelection}
                            setUnitSelection={setUnitSelection}
                            subUnitSelection={subUnitSelection}
                            setSubUnitSelection={setSubUnitSelection}
                            tribeSelection={tribeSelection}
                            setTribeSelection={setTribeSelection}
                            fundingUnitTypeSelection={fundingUnitTypeSelection}
                            setFundingUnitTypeSelection={setFundingUnitTypeSelection}
                            setCostCenterCodeSelection={setCostCenterCodeSelection}
                            setFundingUnitIDSelection={setFundingUnitIDSelection}
                            isNew={isNew}
                            userRoleInfo={userRoleInfo}
                            rows={rows}
                            isBil={isBil}
                            setIsBil={setIsBil}
                            requiredFields={requiredFields}
                            isTreatment={isTreatment}
                            isActual={isActual}
                            isPoint={isPoint}
                            fundingValid={fundingValid}
                            setFundingValid={setFundingValid}
                            isInteragencyRow={isInteragencyRow}
                        />
                        <RecordCostAssignment
                            selectedRow={selectedRow}
                            setSelectedRow={setSelectedRow}
                            personnelCost={personnelCost}
                            setPersonnelCost={setPersonnelCost}
                            otherCost={otherCost}
                            setOtherCost={setOtherCost}
                            assetCost={assetCost}
                            setAssetCost={setAssetCost}
                            grantsFixedCost={grantsFixedCost}
                            setGrantsFixedCost={setGrantsFixedCost}
                            contractualCost={contractualCost}
                            setContractualCost={setContractualCost}
                            totalCost={totalCost}
                            setTotalCost={setTotalCost}
                            isTreatment={isTreatment}
                            isActual={isActual}
                            isNew={isNew}
                            requiredFields={requiredFields}
                            isPoint={isPoint}
                            fundingCostValid={fundingCostValid}
                            setFundingCostValid={setFundingCostValid}
                        />
                        <DialogActions>
                            <Tooltip
                                title={addFundingTooltipText}
                                disableHoverListener={
                                    fundingCostValid && fundingValid && fundingSourceValid
                                }
                            >
                                <div>
                                    <Button
                                        color='primary'
                                        variant='contained'
                                        disabled={
                                            !fundingCostValid ||
                                            !fundingValid ||
                                            !fundingSourceValid
                                        }
                                        style={{
                                            marginTop: '10px',
                                            marginRight: '15px',
                                            width: 100,
                                        }}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            handleSaveFundingCostEntry(e, isNew);
                                        }}
                                    >
                                        Save
                                    </Button>
                                </div>
                            </Tooltip>
                            <Button
                                color='primary'
                                variant='contained'
                                style={{
                                    marginTop: '10px',
                                    marginLeft: '15px',
                                    marginRight: '15px',
                                    width: 100,
                                }}
                                onClick={handleClearFundingCostEntry}
                            >
                                Close
                            </Button>
                        </DialogActions>
                    </Paper>
                </Box>
            </InFormGenericModal>
            <InFormGenericModal
                openFlag={resetAcresPopupOpen}
                closeMethod={() => {
                    setResetAcresPopupOpen(false);
                }}
                buttonMethod={(e) => {
                    e.preventDefault();
                    setResetAcresPopupOpen(false);
                    resetFundingAcres();
                }}
                titleText={'Reset Acres Confirmation'}
                displayButton={true}
                buttonText='Confirm'
                displayCancelButton={true}
            >
                <Box
                    style={{
                        maxHeight: '75vh',
                        overflow: 'auto',
                        maxWidth: '50vw',
                        minWidth: '50vw',
                        marginLeft: '5px',
                    }}
                >
                    <Box>
                        <span>
                            This will reset the acres to the default based on a calculation that
                            sets the acreage to be proportionate to the percentage of cost for each
                            line.
                        </span>
                    </Box>
                    <Box sx={{ whiteSpace: 'pre-wrap' }}>
                        {'\n'}
                        {'Are you sure you want to proceed?'}
                    </Box>
                    <Box></Box>
                </Box>
            </InFormGenericModal>
            <FundingAcresPopup
                openFlag={openFundingAcresEdit}
                handleClearFundingAcres={handleClearFundingAcres}
                fundingRows={rows}
                setFundingRows={setRows}
                editableFeature={editableFeature}
                userInfo={userInfo}
                areFundingGridsLoading={isFundingCostLoading}
                setAreFundingGridsLoading={setIsFundingRowSaving}
                setSnackbarMessage={setSnackbarMessage}
                setSnackbarOpen={setSnackbarOpen}
                setSnackbarSeverity={setSnackbarSeverity}
                featureUnsavedFields={featureUnsavedFields}
            />
            <InFormAlert
                openFlag={openDeleteConfirmation}
                closeMethod={handleCancelDelete}
                buttonMethod={(e) => {
                    e.preventDefault();
                    handleDelete();
                }}
                titleText={'Cost Cost Row Deletion'}
                contentText={'Are you sure you would like to delete this row?'}
            />
            <ThemeProvider theme={theme}>
                <Snackbar
                    open={snackbarOpen}
                    autoHideDuration={6000}
                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                    className={classes.snackBarStyle}
                    onClose={() => {
                        setSnackbarOpen(false);
                        setSnackbarMessage('');
                    }}
                >
                    <Alert
                        onClose={() => {
                            setSnackbarOpen(false);
                            setSnackbarMessage('');
                        }}
                        severity={snackbarSeverity}
                        sx={{ width: '100%' }}
                    >
                        {snackbarMessage}
                    </Alert>
                </Snackbar>
            </ThemeProvider>
        </Box>
    );
}
